import _ from 'lodash'
import axios, { authorize, deleteHeader } from '@/services/axios'
import { setUserId, setUserProperties } from '@firebase/analytics'
import firebaseAnalytics from '@/services/firebase-analytics'
import router from "@/router";
import FeatureFlagService from "@/services/feature-flags";

function trackUser (user) {
  let cleanUser = _.omit(user, ['token'])

  setUserId(firebaseAnalytics.analytics, cleanUser.id)

  setUserProperties(firebaseAnalytics.analytics, { ...cleanUser })

  window.dataLayer?.push({
    event: 'IdentifyUser',
    'DLV - User Email': cleanUser.email,
    'DLV - User ID': cleanUser.id,
    'DLV - User Name': `${cleanUser.fname} ${cleanUser.lname}`,
    'DLV - User Phone': cleanUser.phone,
    'DLV - User Avatar': cleanUser.avatar,
    'DLV - User Company': cleanUser.corporate_id
  })
}

const defaultState = () => {
  return {
    user: null,
    accessToken: localStorage.getItem('__access_token')
  }
}

const state = defaultState()

const getters = {
  user: state => {
    return state.user
  },
  token: state => {
    return state.accessToken
  },
  authenticated: state => {
    return !!state.accessToken || !!state.user
  }
}

const actions = {
  async init ({ dispatch }) {
    try {
      await dispatch('initUser')

      dispatch('app/init', null, { root: true })

      return Promise.resolve()
    } catch {
      return Promise.reject()
    }
  },
  register ({ commit, dispatch }, payload) {
    return new Promise((resolve, reject) => {
      axios
        .post('/users', payload)
        .then(res => {
          commit('AUTH_SUCCESS', res.data)

          commit('app/setWallet', res.data.wallet, { root: true })
          dispatch('app/fetchWalletAccountNumbers', null, {root: true});

          const user = _.omit(res.data, ['walletHistory', 'wallet'])

          trackUser(user)

          firebaseAnalytics.logEvent('successful_registration', {
            ..._.omit(user, ['token'])
          })

          resolve()
        })
        .catch(err => {
          localStorage.clear()

          reject(err)
        })
    })
  },
  login ({ commit, dispatch }, payload) {
    return new Promise((resolve, reject) => {
      axios
        .post('/login', payload)
        .then(res => {
          commit('AUTH_SUCCESS', res.data)

          commit('app/setWallet', res.data.wallet, { root: true })
          dispatch('app/fetchWalletAccountNumbers', null, {root: true});

          const user = _.omit(res.data, ['walletHistory', 'wallet'])

          trackUser(user)

          firebaseAnalytics.logEvent('successful_login', {
            ..._.omit(user, ['token'])
          })

          resolve()
        })
        .catch(err => {
          localStorage.clear()

          reject(err)
        })
    })
  },
  otpLogin ({ commit, dispatch }, payload) {
    return new Promise((resolve, reject) => {
      axios
        .patch(`/otp-login/${payload.referenceId}`, {otp_code: payload.otpCode, type: payload.type})
        .then(res => {
          commit('AUTH_SUCCESS', res.data)

          commit('app/setWallet', res.data.wallet, { root: true })
          dispatch('app/fetchWalletAccountNumbers', null, {root: true});

          const user = _.omit(res.data, ['walletHistory', 'wallet'])

          trackUser(user)

          firebaseAnalytics.logEvent('successful_login', {
            ..._.omit(user, ['token'])
          })

          resolve()
        })
        .catch(err => {
          localStorage.clear()

          reject(err)
        })
    })
  },
  initUser ({ commit, dispatch }) {
    return new Promise((resolve, reject) => {
        const userId = localStorage.getItem('__user_id');
        const endpoint = userId ? `/users/${userId}/wallet` : '/users/profile';
        axios
          .get(endpoint)
          .then(res => {
              commit('AUTH_SUCCESS', res.data)
              if (res.data.wallet) {
                  commit('app/setWallet', res.data.wallet, { root: true })
                  dispatch('app/fetchWalletAccountNumbers', null, { root: true })
              }
              if (res.data.walletHistory) {
                  commit('app/setWalletHistory', res.data.walletHistory, {
                      root: true
                  });
              }
              const user = _.omit(res.data, ['walletHistory', 'wallet']);
              trackUser(user);
              resolve(user);
          })
          .catch(async err => {
              if(err && err.response?.status === 403) {
                  await dispatch('logout');
                  router.push({ name: "login" }).catch(() => {});
              }
              reject(err);
          });
    })
  },
  updateUser ({ commit }, data) {
    return new Promise((resolve, reject) => {
      const userId = localStorage.getItem('__user_id')

      axios
        .patch(`/users/${userId}`, data)
        .then(res => {
          commit('SET_USER', res.data)

          resolve(res.data)
        })
        .catch(err => {
          reject(err)
        })
    })
  },
  updateAvatar ({ commit }, data) {
    return new Promise((resolve, reject) => {
      const userId = localStorage.getItem('__user_id')

      axios
        .patch(`/user/avatar/${userId}`, data)
        .then(res => {
          commit('SET_USER', res.data)

          resolve(res.data)
        })
        .catch(err => {
          reject(err)
        })
    })
  },
  logout ({ commit }) {
    return axios.delete('/logout').finally(() => {
      localStorage.clear()

      commit('LOGOUT')

      deleteHeader('Authorization')
    })
  }
}

const mutations = {
  AUTH_SUCCESS (state, data) {
    if (data.token) {
      const { token, exp } = data.token;
      state.accessToken = token;
      authorize(token);
      localStorage.setItem('__token_expiry', exp);
      localStorage.setItem('__access_token', token);
    }
    state.user = _.omit(data, ['token', 'wallet']);
    localStorage.setItem('__user_id', state.user.id);
    FeatureFlagService.setCurrentUserData({
      email: data?.email,
      userId: data?.id
    });
  },
  SET_USER (state, user) {
    state.user = user
  },
  LOGOUT (state) {
    Object.assign(state, defaultState())
  },
  reset (state) {
    Object.assign(state, defaultState())
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
